import { fireClearCodeEvent, goToPage } from "../../../editor/events";

import type { StepType } from "@reactour/tour";
import { typeSomeText } from "../../common";
import { switchToLevel } from "../ai-first/helpers";
import TourStepContent from "./wrapper";
import { text, translateJadleToLocale } from "@quickpoint.me/shared";

const t = () => text.tourSteps;

function getActionTexts() {
	const s = t();
	return [
		s.basicsHelloClass,
		translateJadleToLocale(`title: ${s.basicsHelloClass}`),
		translateJadleToLocale("\n  color: third"),
		translateJadleToLocale(`background: sigmund\n  blur: 5\n  opacity: 90\ntitle: ${s.basicsYourWords}\n  shadow:over-image\n#:${s.basicsPowerfullyExpressive}\n  shadow:over-image\n#: ${s.basicsEssentialCivilisation}\n  shadow:over-image\nblank:4\n`),
		translateJadleToLocale(`\nsubtitle: ${s.basicsCivilizationQuote}\n  size: 5  \n  shadow:over-image\n${s.basicsSigmundFreud}\n  align:right\n  size: 4\n  shadow:over-image`),
		"\n===",
		translateJadleToLocale(`\ntitle: ${s.basicsPage2Title}\n${s.basicsPage2Content}\n`),
		translateJadleToLocale(`\nsupertitle: ${s.basicsSupertitle} \ntitle: ${s.basicsThisIsTitle} \nsubtitle: ${s.basicsThisIsSmaller}  \n${s.basicsSomeParagraph}`),
		translateJadleToLocale("  color: third\n. align:right\n"),
		"", // placeholder — not typed into editor
		translateJadleToLocale(`image:bauhaus interior\n  caption: ${s.bauhausInteriorCaption}\n|\nsupertitle: ${s.basicsFiltersTitle}\ntitle: ${s.basicsCanMakeIdeas}\nsubtitle: ${s.basicsFeelDifferent}\n${s.basicsFiltersParagraph}`),
		translateJadleToLocale("\nfilter:quickpoint::tufte\n\n"),
		translateJadleToLocale("\nfilter:quickpoint::caruso-italia\n\n"),
		translateJadleToLocale("\nfilter:quickpoint::nyt\n\n"),
		translateJadleToLocale(" align: left"),
	];
}

const basics = (hostId: string): StepType[] => [
	{
		selector: ".author-root",
		position: "center",
		content: () => (
			<TourStepContent description={() => <strong>{text.tourSteps.basicsTitle}</strong>} />
		),
	},
	{
		selector: ".author-root",
		position: "center",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsWelcomeAccessibility}
						<br />
						<br />
						{text.tourSteps.basicsWatchViewer}
						<br />
						<br />
						<small>{text.tourSteps.basicsAccessibilityFootnote}</small>
					</span>
				)}
			/>
		),
	},
	{
		selector: ".qp-editor",
		position: "center",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsYouJustType}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			typeSomeText({ text: t().basicsHelloClass, prefill: "" });
		},
	},
	{
		selector: "#landscape-viewer",
		position: "left",
		content: () => (
			<TourStepContent
				description={() => <span>{text.tourSteps.basicsWordsAppearInstantly}</span>}
			/>
		),
	},
	{
		selector: ".qp-editor",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsAddLabel}
					</span>
				)}
			/>
		),
		action: () => {
			switchToLevel(3);
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: a[1], prefill: "" });
		},
	},
	{
		selector: "#landscape-viewer",
		position: "left",
		content: () => (
			<TourStepContent
				description={() => <span>{text.tourSteps.basicsItsATitle}</span>}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: "", prefill: a[1] });
		},
	},
	{
		selector: "#landscape-viewer",
		position: "left",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsChangeColor}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: a[2], prefill: a[1] });
		},
	},
	{
		selector: ".cm-line:nth-of-type(2)",
		position: "left",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsIndentAndColor} <span className="qp-tour-modifier">color:</span> {text.tourSteps.basicsToSeeOptions}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: "", prefill: a[1] + a[2] });
		},
	},
	{
		selector: ".qp-editor",
		position: "right",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsTypePrefix} <span className="qp-tour-modifier">===</span> {text.tourSteps.basicsTypeNewPage}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({
				text: a[5] + a[6],
				prefill: a[1] + a[2],
			});
			goToPage(hostId, "QP", 2);
		},
	},
	{
		selector: "#vocabbar",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsThisIsThe} <strong>Vocab Bar</strong>{text.tourSteps.basicsVocabBarIntro}
					</span>
				)}
			/>
		),
	},
	{
		selector: "#vocabbar",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsVocabBarUsage}
					</span>
				)}
			/>
		),
	},
	{
		selector: "#landscape-viewer",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsChangeMoodPrefix} <span className="qp-tour-keyword">filter:</span> {text.tourSteps.basicsChangeMoodSuffix}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: "", prefill: a[10] });
			goToPage(hostId, "QP", 1);
		},
	},
	{
		selector: "#landscape-viewer",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsDifferentFilters}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: "", prefill: a[11] + a[10] });
			goToPage(hostId, "QP", 1);
		},
	},
	{
		selector: "#landscape-viewer",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsHighContrast}
					</span>
				)}
			/>
		),
		action: () => {
			fireClearCodeEvent();
			const a = getActionTexts();
			typeSomeText({ text: "", prefill: a[12] + a[10] });
		},
	},
	{
		selector: ".author-root",
		position: "center",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.basicsCoveredBasics}
					</span>
				)}
			/>
		),
	},
];

export default basics;
